/**
 * useSimulaData - Hook central de dados reais com backend autenticado.
 */

import { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { DataContext } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { isConcludedAuditStatus } from '../utils/auditStatus';
import type {
  AuditGroup,
  Audit,
  Goal,
  Operator,
  Supervisor,
  Sector,
  Monitor,
  Scenario,
  ScorecardItem,
  Certificate,
  League,
  LeagueParticipant,
  Season,
  TrainingSession,
  DashboardWidget,
  MonitorGoal,
  MonitorPerformance,
  AuditQueueItem,
  SupervisorAlert,
  PendingApproval,
  TeamWeeklyTrend,
  TeamActivity,
  SupervisorCoachingNote,
  TrainingEvent,
  Feedback,
  Notification,
  Script,
  Questionnaire,
  QuestionnaireResponse,
  HierarchyAssignment,
  HierarchyEntityType,
  Badge,
  OperatorBadge,
  SystemUser,
} from '../types/domain';
import {
  getActiveSectors,
  getActiveSupervisors,
  getEntityHistory,
  getHierarchyAtDate,
  createAssignment,
  endAssignment,
} from '../utils/hierarchy';
import { isActionableApprovalStatus } from '../utils/approvalStatus';

// Mapping from internal camelCase entity keys to kebab-case API entity names
const ENTITY_API_NAMES: Record<string, string> = {
  audits: 'audits',
  auditGroups: 'audit-groups',
  goals: 'goals',
  operators: 'operators',
  supervisors: 'supervisors',
  sectors: 'sectors',
  monitors: 'monitors',
  scenarios: 'scenarios',
  scorecard: 'scorecard',
  certificates: 'certificates',
  leagues: 'leagues',
  leagueParticipants: 'league-participants',
  seasons: 'seasons',
  trainingSessions: 'training-sessions',
  dashboardWidgets: 'dashboard-widgets',
  monitorGoals: 'monitor-goals',
  monitorPerformance: 'monitor-performance',
  auditQueue: 'audit-queue',
  supervisorAlerts: 'supervisor-alerts',
  pendingApprovals: 'pending-approvals',
  teamWeeklyTrends: 'team-weekly-trends',
  teamActivities: 'team-activities',
  coachingNotes: 'coaching-notes',
  hierarchyHistory: 'hierarchy-history',
  questionnaires: 'questionnaires',
  questionnaireResponses: 'questionnaire-responses',
  trainingEvents: 'training-events',
  feedbacks: 'feedbacks',
  notifications: 'notifications',
  scripts: 'scripts',
  users: 'users',
  badges: 'badges',
  operatorBadges: 'operator-badges',
};

// Entity type mapping
type EntityMap = {
  audits: Audit;
  auditGroups: AuditGroup;
  goals: Goal;
  operators: Operator;
  supervisors: Supervisor;
  sectors: Sector;
  monitors: Monitor;
  scenarios: Scenario;
  scorecard: ScorecardItem;
  certificates: Certificate;
  leagues: League;
  leagueParticipants: LeagueParticipant;
  seasons: Season;
  trainingSessions: TrainingSession;
  dashboardWidgets: DashboardWidget;
  monitorGoals: MonitorGoal;
  monitorPerformance: MonitorPerformance;
  auditQueue: AuditQueueItem;
  supervisorAlerts: SupervisorAlert;
  pendingApprovals: PendingApproval;
  teamWeeklyTrends: TeamWeeklyTrend;
  teamActivities: TeamActivity;
  coachingNotes: SupervisorCoachingNote;
  hierarchyHistory: HierarchyAssignment;
  questionnaires: Questionnaire;
  questionnaireResponses: QuestionnaireResponse;
  trainingEvents: TrainingEvent;
  feedbacks: Feedback;
  notifications: Notification;
  scripts: Script;
  users: SystemUser;
  badges: Badge;
  operatorBadges: OperatorBadge;
};

/**
 * Hook genérico para uma entidade específica.
 * Retorna [data, { add, update, remove, refresh }]
 */
export function useEntityData<K extends keyof EntityMap>(entity: K) {
  type T = EntityMap[K];
  const dataCtx = useContext(DataContext);
  const { user, isAuthLoading } = useAuth();
  // Graceful fallback when DataProvider is not available (e.g. during HMR)
  const fetchAll = dataCtx?.fetchAll ?? (async () => [] as any[]);
  const save = dataCtx?.save ?? (async () => null as any);
  const remove = dataCtx?.remove ?? (async () => false);
  const isOnline = dataCtx?.isOnline ?? true;
  const apiName = ENTITY_API_NAMES[entity] || entity;
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const lastAuthScope = useRef<string | null>(null);

  // Single fetch per load. A previous 3x retry-on-empty tripled the request
  // volume: an empty result is a valid state for many entities, and when the
  // backend returned 429 (rate limited) the retries fired again with no backoff,
  // amplifying the storm that made data vanish. One attempt keeps the fan-out small.
  const fetchAllWithRetry = useCallback(async (): Promise<T[]> => {
    const remote = await fetchAll<T>(apiName);
    return remote || [];
  }, [apiName, fetchAll]);

  // Refetch whenever the authenticated user changes to avoid stale cross-session caches.
  useEffect(() => {
    if (isAuthLoading) return;

    const authScope = user ? `${user.id}:${user.role}` : 'anonymous';
    if (lastAuthScope.current === authScope) return;
    lastAuthScope.current = authScope;

    if (!user) {
      setData([]);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const remote = await fetchAllWithRetry();
        setData(remote || []);
      } catch (e) {
        console.error(`[useEntityData] Failed to fetch ${entity}:`, e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [entity, fetchAllWithRetry, isAuthLoading, isOnline, user]);

  const add = useCallback(async (item: T): Promise<T> => {
    if (isOnline) {
      const saved = await save(apiName, item as T & { id?: string });
      if (!saved) {
        throw new Error(`Falha ao persistir ${String(entity)}.`);
      }
      setData(prev => [...prev, saved as T]);
      return saved as T;
    }

    setData(prev => [...prev, item]);
    return item;
  }, [entity, apiName, save, isOnline]);

  const update = useCallback(async (id: string, updates: Partial<T>) => {
    if (isOnline) {
      const existing = data.find((item: any) => item.id === id);
      if (!existing) {
        throw new Error(`${String(entity)}/${id} nao encontrado para atualizar.`);
      }
      const saved = await save(apiName, { ...existing, ...updates } as T & { id: string });
      if (!saved) {
        throw new Error(`Falha ao atualizar ${String(entity)}/${id}.`);
      }
      setData(prev => prev.map(item => ((item as any).id === id ? (saved as T) : item)));
      return;
    }
    setData(prev => prev.map(item => ((item as any).id === id ? { ...item, ...updates } : item)));
  }, [entity, apiName, save, isOnline, data]);

  const removeItem = useCallback(async (id: string) => {
    if (isOnline) {
      const ok = await remove(apiName, id);
      if (!ok) {
        throw new Error(`Falha ao remover ${String(entity)}/${id}.`);
      }
      setData(prev => prev.filter((item: any) => item.id !== id));
      return;
    }
    setData(prev => prev.filter((item: any) => item.id !== id));
  }, [entity, apiName, remove, isOnline]);

  const refresh = useCallback(async () => {
    if (!isOnline) return;
    setLoading(true);
    try {
      const remote = await fetchAllWithRetry();
      setData(remote || []);
    } catch {
      // keep local
    } finally {
      setLoading(false);
    }
  }, [fetchAllWithRetry, isOnline]);

  const setAll = useCallback((items: T[]) => {
    setData(items);
  }, []);

  return { data, loading, add, update, remove: removeItem, refresh, setAll, setData: setAll };
}

/**
 * Hook de alto nível com dados pré-carregados e helpers de hierarquia.
  */
export function useSimulaData() {
  const operators = useEntityData('operators');
  const supervisors = useEntityData('supervisors');
  const sectors = useEntityData('sectors');
  const monitors = useEntityData('monitors');
  const audits = useEntityData('audits');
  const auditGroups = useEntityData('auditGroups');
  const goals = useEntityData('goals');
  const certificates = useEntityData('certificates');
  const trainingSessions = useEntityData('trainingSessions');
  const scenarios = useEntityData('scenarios');
  const scorecard = useEntityData('scorecard');
  const leagues = useEntityData('leagues');
  const leagueParticipants = useEntityData('leagueParticipants');
  const seasons = useEntityData('seasons');
  const hierarchy = useEntityData('hierarchyHistory');
  const questionnaires = useEntityData('questionnaires');
  const questionnaireResponses = useEntityData('questionnaireResponses');

  // v3 entities
  const dashboardWidgets = useEntityData('dashboardWidgets');
  const monitorGoals = useEntityData('monitorGoals');
  const monitorPerformance = useEntityData('monitorPerformance');
  const auditQueue = useEntityData('auditQueue');
  const supervisorAlerts = useEntityData('supervisorAlerts');
  const pendingApprovals = useEntityData('pendingApprovals');
  const teamWeeklyTrends = useEntityData('teamWeeklyTrends');
  const teamActivities = useEntityData('teamActivities');
  const coachingNotes = useEntityData('coachingNotes');

  // v4 entities
  const trainingEvents = useEntityData('trainingEvents');
  const feedbacks = useEntityData('feedbacks');
  const notifications = useEntityData('notifications');
  const scripts = useEntityData('scripts');
  const users = useEntityData('users');
  const badges = useEntityData('badges');
  const operatorBadges = useEntityData('operatorBadges');

  // === Hierarchy helpers ===

  const getOperatorActiveSectors = useCallback(
    (operatorId: string) => getActiveSectors('operator', operatorId, hierarchy.data),
    [hierarchy.data]
  );

  const getOperatorActiveSupervisors = useCallback(
    (operatorId: string) => getActiveSupervisors(operatorId, hierarchy.data),
    [hierarchy.data]
  );

  const getSupervisorActiveSectors = useCallback(
    (supervisorId: string) => getActiveSectors('supervisor', supervisorId, hierarchy.data),
    [hierarchy.data]
  );

  const getMonitorActiveSectors = useCallback(
    (monitorId: string) => getActiveSectors('monitor', monitorId, hierarchy.data),
    [hierarchy.data]
  );

  const getEntityHierarchyHistory = useCallback(
    (entityType: HierarchyEntityType, entityId: string) =>
      getEntityHistory(entityType, entityId, hierarchy.data),
    [hierarchy.data]
  );

  const getHierarchyAt = useCallback(
    (entityType: HierarchyEntityType, entityId: string, date: string | Date) =>
      getHierarchyAtDate(entityType, entityId, date, hierarchy.data),
    [hierarchy.data]
  );

  const addHierarchyAssignment = useCallback(
    async (
      entityType: HierarchyEntityType,
      entityId: string,
      relationType: 'sector' | 'supervisor',
      relationId: string,
      reason: string,
      createdBy: string
    ) => {
      const assignment = await hierarchy.add(
        createAssignment(entityType, entityId, relationType, relationId, reason, createdBy) as HierarchyAssignment,
      );
      return assignment as HierarchyAssignment;
    },
    [hierarchy]
  );

  const endHierarchyAssignment = useCallback(
    async (assignmentId: string, reason: string) => {
      const existing = hierarchy.data.find(h => h.id === assignmentId);
      if (!existing) return;
      const ended = endAssignment(existing, reason);
      await hierarchy.update(assignmentId, ended);
    },
    [hierarchy]
  );

  // === Lookup helpers ===

  const getOperator = useCallback(
    (id: string) => operators.data.find(op => op.id === id),
    [operators.data]
  );

  const getSupervisor = useCallback(
    (id: string) => supervisors.data.find(s => s.id === id),
    [supervisors.data]
  );

  const getSector = useCallback(
    (id: string) => sectors.data.find(s => s.id === id),
    [sectors.data]
  );

  const getMonitor = useCallback(
    (id: string) => monitors.data.find(m => m.id === id),
    [monitors.data]
  );

  const getSectorName = useCallback(
    (id: string) => sectors.data.find(s => s.id === id)?.name || id,
    [sectors.data]
  );

  const getSupervisorName = useCallback(
    (id: string) => supervisors.data.find(s => s.id === id)?.name || id,
    [supervisors.data]
  );

  const getOperatorName = useCallback(
    (id: string) => operators.data.find(op => op.id === id)?.name || id,
    [operators.data]
  );

  // === Derived stats ===

  const operatorStats = useMemo(() => {
    const stats: Record<string, {
      avgTrainingScore: number;
      avgAuditScore: number;
      totalTrainings: number;
      totalSessions: number;
      totalAudits: number;
      falhaGraveCount: number;
      zeroScorePercentage: number;
      lastActivityDate: string;
      trend: 'up' | 'down' | 'stable';
    }> = {};

    operators.data.forEach(op => {
      const opAudits = audits.data.filter(a => a.operatorId === op.id && a.evaluation && isConcludedAuditStatus(a.status));
      const opTrainings = trainingSessions.data.filter(t => t.operatorId === op.id && t.evaluation);
      const avgAudit = opAudits.length > 0
        ? Math.round(opAudits.reduce((sum, a) => sum + (a.evaluation?.score || 0), 0) / opAudits.length)
        : 0;
      const avgTraining = opTrainings.length > 0
        ? Math.round(opTrainings.reduce((sum, t) => sum + (t.evaluation?.score || 0), 0) / opTrainings.length)
        : 0;
      const fgCount = opAudits.filter(a => a.evaluation?.hasFalhaGrave).length;
      const zeroCount = [...opAudits, ...opTrainings].filter(item => item.evaluation?.score === 0).length;
      const totalEvals = opAudits.length + opTrainings.length;
      const zeroPercentage = totalEvals > 0 ? Math.round((zeroCount / totalEvals) * 100) : 0;

      const allItems = [...opAudits, ...opTrainings].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const recentHalf = allItems.slice(0, Math.ceil(allItems.length / 2));
      const olderHalf = allItems.slice(Math.ceil(allItems.length / 2));
      const recentAvg = recentHalf.length > 0 ? recentHalf.reduce((s, i) => s + (i.evaluation?.score || 0), 0) / recentHalf.length : 0;
      const olderAvg = olderHalf.length > 0 ? olderHalf.reduce((s, i) => s + (i.evaluation?.score || 0), 0) / olderHalf.length : 0;
      const trend: 'up' | 'down' | 'stable' = recentAvg > olderAvg + 5 ? 'up' : recentAvg < olderAvg - 5 ? 'down' : 'stable';
      const lastDate = allItems.length > 0 ? allItems[0].date : '';

      stats[op.id] = {
        avgTrainingScore: avgTraining,
        avgAuditScore: avgAudit,
        totalTrainings: opTrainings.length,
        totalSessions: opTrainings.length + opAudits.length,
        totalAudits: opAudits.length,
        falhaGraveCount: fgCount,
        zeroScorePercentage: zeroPercentage,
        lastActivityDate: lastDate,
        trend,
      };
    });
    return stats;
  }, [operators.data, audits.data, trainingSessions.data]);

  // === Supervisor helpers ===

  const getAlertsBySupervisor = useCallback(
    (supervisorId: string) =>
      supervisorAlerts.data
        .filter(a => a.supervisorId === supervisorId)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [supervisorAlerts.data]
  );

  const getPendingApprovalsBySupervisor = useCallback(
    (supervisorId: string) =>
      pendingApprovals.data.filter(
        (p) => p.supervisorId === supervisorId && isActionableApprovalStatus(p.status),
      ),
    [pendingApprovals.data]
  );

  const getWeeklyTrendsBySupervisor = useCallback(
    (supervisorId: string) =>
      teamWeeklyTrends.data.filter(t => t.supervisorId === supervisorId),
    [teamWeeklyTrends.data]
  );

  const getActivitiesBySupervisor = useCallback(
    (supervisorId: string) =>
      teamActivities.data
        .filter(a => a.supervisorId === supervisorId)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [teamActivities.data]
  );

  const getCoachingNotesBySupervisor = useCallback(
    (supervisorId: string) =>
      coachingNotes.data
        .filter(n => n.supervisorId === supervisorId)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [coachingNotes.data]
  );

  // === Operator helpers ===

  const getGoalsByOperator = useCallback(
    (operatorId: string) => {
      const op = operators.data.find(o => o.id === operatorId);
      if (!op) return [];
      return goals.data.filter(g => {
        if (g.scope === 'individual' && g.operatorId === operatorId) return true;
        if (g.scope === 'team' && g.supervisorId === op.supervisorId) return true;
        if (g.scope === 'sector' && g.sectorId === op.sectorId) return true;
        if (g.scope === 'global') return true;
        return false;
      });
    },
    [operators.data, goals.data]
  );

  const getRankingData = useCallback(() => {
    return operators.data.map(op => {
      const opAudits = audits.data.filter(a => a.operatorId === op.id && a.evaluation && isConcludedAuditStatus(a.status));
      const opTrainings = trainingSessions.data.filter(t => t.operatorId === op.id && t.evaluation);
      const allEvals = [...opAudits.map(a => a.evaluation!), ...opTrainings.map(t => t.evaluation!)];
      const avgScore = allEvals.length > 0
        ? Math.round(allEvals.reduce((sum, e) => sum + e.score, 0) / allEvals.length)
        : 0;
      const participant = leagueParticipants.data.find(lp => lp.operatorId === op.id);
      const fgRate = opAudits.length > 0
        ? Math.round((opAudits.filter(a => a.evaluation?.hasFalhaGrave).length / opAudits.length) * 100)
        : 0;

      return {
        id: op.id,
        name: op.name,
        supervisorId: op.supervisorId,
        sectorId: op.sectorId,
        score: avgScore,
        auditsCompleted: opAudits.length,
        trainingSessions: opTrainings.length,
        badges: 0,
        streak: participant?.streak || 0,
        falhaGraveRate: fgRate,
        improvement: 0,
      };
    }).sort((a, b) => b.score - a.score);
  }, [operators.data, audits.data, trainingSessions.data, leagueParticipants.data]);

  return {
    // Entity data + CRUD
    operators, supervisors, sectors, monitors, audits, auditGroups, goals,
    certificates, trainingSessions, scenarios, scorecard,
    leagues, leagueParticipants, seasons, hierarchy,
    questionnaires, questionnaireResponses,

    // v3 entities
    dashboardWidgets, monitorGoals, monitorPerformance,
    auditQueue, supervisorAlerts, pendingApprovals,
    teamWeeklyTrends, teamActivities, coachingNotes,

    // v4 entities
    trainingEvents, feedbacks, notifications, scripts,
    users,
    badges, operatorBadges,

    // Hierarchy helpers
    getOperatorActiveSectors,
    getOperatorActiveSupervisors,
    getSupervisorActiveSectors,
    getMonitorActiveSectors,
    getEntityHierarchyHistory,
    getHierarchyAt,
    addHierarchyAssignment,
    endHierarchyAssignment,

    // Lookup helpers
    getOperator, getSupervisor, getSector, getMonitor,
    getSectorName, getSupervisorName, getOperatorName,

    // Derived
    operatorStats,
    getRankingData,

    // Supervisor helpers
    getAlertsBySupervisor,
    getPendingApprovalsBySupervisor,
    getWeeklyTrendsBySupervisor,
    getActivitiesBySupervisor,
    getCoachingNotesBySupervisor,

    // Operator helpers
    getGoalsByOperator,
  };
}


